import type { HelpArticleDef } from "./articleKit";
import {
  AppLink,
  ArticleLink,
  P,
  Sect,
  Steps,
  Tip,
  Ui,
} from "./articleKit";

export const INSIGHT_ARTICLES: HelpArticleDef[] = [
  {
    slug: "reports-and-attribution",
    title: "Reports and attribution",
    category: "Reports & insight",
    minutes: 6,
    summary:
      "The report suite: scorecard, attribution models, web and ads, and how to answer 'what's actually driving revenue?'",
    keywords: ["analytics", "revenue", "channels", "first touch", "last touch", "roas", "funnel"],
    related: ["dashboards-and-report-builder", "forms", "integrations"],
    body: (
      <>
        <P>
          Reports exist to answer one question honestly: what is actually
          producing pipeline and revenue? The suite opens from the{" "}
          <Ui>Reports</Ui> menu in the top nav; every report has filters
          (date range and more) in a bar at the top, a <Ui>Reset</Ui> when
          you've wandered, and a methodology note explaining exactly how its
          numbers are computed.
        </P>
        <Sect title="Scorecard">
          <P>
            <AppLink to="/reports/scorecard">The scorecard</AppLink> is the
            business at a glance: pipeline created, weighted forecast, win
            rate, revenue won, loss reasons, and funnel conversion. It reads
            from your deals and{" "}
            <ArticleLink slug="contacts-and-companies">
              lifecycle stages
            </ArticleLink>
            , so it's only as honest as your pipeline hygiene.
          </P>
        </Sect>
        <Sect title="Attribution">
          <P>
            <AppLink to="/reports/attribution">Attribution</AppLink> connects
            marketing touches to closed revenue. Pick a model: first touch
            credits the channel that found them, last touch credits the one
            that converted them, and linear spreads credit across the
            journey. Slice by channel, source, or campaign, and filter to won
            deals to see what actually pays. Touches come from{" "}
            <ArticleLink slug="forms">form fills</ArticleLink> and the
            tracking data on each contact.
          </P>
        </Sect>
        <Sect title="Web & Ads">
          <P>
            <AppLink to="/reports/web">Web &amp; Ads</AppLink> joins site
            traffic and ad performance against your pipeline once the Google
            Analytics, Search Console, and ads{" "}
            <ArticleLink slug="integrations">integrations</ArticleLink> are
            connected: sessions on one side, cost per opportunity and ROAS on
            the other.
          </P>
        </Sect>
        <Sect title="Quick answers">
          <P>
            The{" "}
            <AppLink to="/reports/library">reports library</AppLink> opens
            with one-click questions ("Which channel closed the most revenue
            this quarter?") that jump straight to the right report with the
            right filters already applied. Start there before building
            anything custom.
          </P>
        </Sect>
      </>
    ),
  },
  {
    slug: "dashboards-and-report-builder",
    title: "Dashboards and the report builder",
    category: "Reports & insight",
    minutes: 4,
    summary:
      "Build your own reports from any object and filter set, then pin them as tiles on shareable dashboards.",
    keywords: ["custom report", "charts", "tiles", "widgets", "measure", "group by"],
    related: ["reports-and-attribution", "lists-and-segments"],
    body: (
      <>
        <P>
          When the built-in reports don't answer your exact question, build
          it. The{" "}
          <AppLink to="/reports/builder">report builder</AppLink> works like
          a sentence: count (or sum) which object, grouped by what, filtered
          how, drawn as which chart.
        </P>
        <Sect title="Building a report">
          <Steps
            items={[
              <>
                Pick the object (contacts, companies, deals) and the measure:
                count, or a sum like deal amount.
              </>,
              <>
                Group by any field (owner, stage, channel, lifecycle stage, a
                custom field) and stack filters with the same conditions{" "}
                <ArticleLink slug="lists-and-segments">lists</ArticleLink>{" "}
                use.
              </>,
              <>
                Choose the chart: stat, column, bar, line, area, donut,
                funnel, or table. Save it with a name.
              </>,
            ]}
          />
        </Sect>
        <Sect title="Dashboards">
          <P>
            Saved reports become tiles. Dashboards live on the{" "}
            <AppLink to="/">home screen</AppLink>: the selector in its upper
            right switches between them, creates new ones, and edits the
            current one - add tiles from your saved reports, set each to half
            or full width, and override the chart type per tile. The{" "}
            <AppLink to="/reports/library">reports library</AppLink> is where
            ready-made dashboard templates install from. Build one dashboard
            per audience: a Monday-morning view for you, a pipeline wall for
            the team.
          </P>
          <Tip>
            A dashboard earns its keep when it changes a decision. If a tile
            hasn't changed what anyone does in a month, replace it.
          </Tip>
        </Sect>
      </>
    ),
  },
  {
    slug: "keep-an-eye-on",
    title: "The Keep an eye on panel",
    category: "Reports & insight",
    minutes: 4,
    summary:
      "The dashboard's watchlist: how heating up, slipping deals, and overdue follow-ups are picked, and how to tune every threshold.",
    keywords: ["signals", "watchlist", "heating up", "slipping", "overdue", "alerts", "thresholds"],
    related: ["workflows-and-lead-scoring", "deals-and-pipeline", "tasks-and-calendar"],
    body: (
      <>
        <P>
          At the top of the <AppLink to="/">Dashboard</AppLink> sits "Keep an
          eye on": three columns of things that deserve attention today.
          Nothing in it is a black box - every item is picked by a rule you
          can read and change.
        </P>
        <Sect title="The three columns">
          <P>
            <Ui>Heating up</Ui> is your contacts whose{" "}
            <ArticleLink slug="workflows-and-lead-scoring">
              lead score
            </ArticleLink>{" "}
            clears a threshold (45 by default) and who were active recently
            (14 days by default), ranked by score. Hover the badge on any of
            them to see exactly which signals earned the points.{" "}
            <Ui>Slipping deals</Ui> is open deals untouched past a quiet
            window (21 days by default), longest-quiet first, with the value
            at stake. <Ui>Follow-ups overdue</Ui> is your open tasks past
            their due date.
          </P>
        </Sect>
        <Sect title="Tuning it">
          <P>
            Every number above is a setting. Open{" "}
            <AppLink to="/settings/signals">
              Settings → Keep an eye on
            </AppLink>{" "}
            to change the score threshold, the activity and quiet windows, a
            minimum deal size for slipping, a grace period for overdue, how
            many items each column shows, or to switch a column off
            entirely. The panel's info icon shows the current rules in
            plain language wherever you are.
          </P>
          <Tip>
            If the heating-up column feels noisy, raise the score threshold
            before touching the scoring rules; if it feels empty, check that
            your{" "}
            <ArticleLink slug="workflows-and-lead-scoring">
              scoring rules
            </ArticleLink>{" "}
            reflect how your buyers actually behave.
          </Tip>
        </Sect>
      </>
    ),
  },
];
